import { Fragment, useState, useEffect, useMemo } from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, ArrowLeft, ArrowRight, Briefcase, Building2, Calendar, CheckCircle2, DollarSign, Download, FileText, FolderOpen, Globe, Package, Percent, PieChart, Plus, Save, Trash2, TrendingUp, Users, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

export const BusinessCaseModule = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [chartFilters, setChartFilters] = useState({
    sales: true,
    grossProfit: false,
    expenses: false,
    ebitda: true
  });
  const [projectInfo, setProjectInfo] = useState({
    name: '',
    country: 'Mexico',
    product: '',
    partner: '',
    businessModel: 'partner',
    wacc: 10,
    year: new Date().getFullYear()
  });

  const [skus, setSkus] = useState([
    {
      id: 1,
      name: '75MG/1ML',
      type: 'Private',
      unitsY1: '1000',
      unitsY2: '1200',
      unitsY3: '1400',
      unitsY4: '1600',
      unitsY5: '1800',
      valueY1: '10000',
      valueY2: '12000',
      valueY3: '14000',
      valueY4: '16000',
      valueY5: '18000',
      cagr: 0,
      aspLastMat: 0,
      growthY1: 0,
      growthY2: 0,
      growthY3: 0,
      growthY4: 0,
      growthY5: 0,
      msY1: 8,
      msY2: 13,
      msY3: 20,
      msY4: 25,
      msY5: 30,
      aspY1: '',
      aspY2: '',
      aspY3: '',
      aspY4: '',
      aspY5: '',
      aspPerUnit: '',
      aspAdjustY1: 5,
      aspAdjustY2: 5,
      aspAdjustY3: 5,
      aspAdjustY4: 5,
      aspAdjustY5: 5,
      cogsPerUnit: '',
      cogsInflation: 0,
      competitorsY1: '',
      competitorsY2: '',
      competitorsY3: '',
      competitorsY4: '',
      competitorsY5: ''
    }
  ]);

  const [expenses, setExpenses] = useState([
    { id: 1, name: 'Registration', type: 'USD', y0: 10000, y1: 0, y2: 0, y3: 0, y4: 0, y5: 0 },
    { id: 2, name: 'Marketing', type: '%', y0: 0, y1: 10, y2: 10, y3: 10, y4: 10, y5: 10 },
    { id: 3, name: 'Salary (FTE)', type: 'USD', y0: 0, y1: 25000, y2: 25000, y3: 25000, y4: 25000, y5: 25000 },
    { id: 4, name: 'Distribution', type: '%', y0: 0, y1: 8, y2: 8, y3: 8, y4: 8, y5: 8 },
    { id: 5, name: 'Partner Margin', type: '%', y0: 0, y1: 0, y2: 0, y3: 0, y4: 0, y5: 0 }
  ]);

  const [upfrontPayments, setUpfrontPayments] = useState([
    { id: 1, name: 'Upfront Payment', y0: 0, y1: 0, y2: 0, y3: 0, y4: 0, y5: 0 }
  ]);

  const [isPrinting, setIsPrinting] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [savedCases, setSavedCases] = useState([]);
  const [showSavedCases, setShowSavedCases] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Load saved cases on mount
  useEffect(() => {
    loadSavedCases();
  }, []);

  const loadSavedCases = async () => {
    try {
      const { data } = await supabase.from('module_data').select('content').eq('key', 'bc_list').single();
      if (data?.content) setSavedCases(data.content);
    } catch (error) {
      setSavedCases([]);
    }
  };

  const handleSave = async () => {
    if (!projectInfo.name.trim()) {
      setSaveMessage('Please enter a Project Name before saving');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }
    setIsSaving(true);
    try {
      const caseId = `bc-${Date.now()}`;
      const caseData = { id: caseId, projectInfo, skus, expenses, upfrontPayments, savedAt: new Date().toISOString() };
      await supabase.from('module_data').upsert({ key: `bc_${caseId}`, content: caseData }, { onConflict: 'key' });
      const newEntry = { id: caseId, name: projectInfo.name, country: projectInfo.country, product: projectInfo.product, partner: projectInfo.partner, savedAt: caseData.savedAt };
      const updatedList = [...savedCases.filter(c => !(c.name === projectInfo.name && c.country === projectInfo.country)), newEntry];
      await supabase.from('module_data').upsert({ key: 'bc_list', content: updatedList }, { onConflict: 'key' });
      setSavedCases(updatedList);
      setSaveMessage('Business Case saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Error saving Business Case');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoad = async (caseId) => {
    try {
      const { data } = await supabase.from('module_data').select('content').eq('key', `bc_${caseId}`).single();
      if (data?.content) {
        const caseData = data.content;
        setProjectInfo(caseData.projectInfo);
        setSkus(caseData.skus);
        setExpenses(caseData.expenses);
        setUpfrontPayments(caseData.upfrontPayments || [{ id: 1, name: 'Upfront Payment', y0: 0, y1: 0, y2: 0, y3: 0, y4: 0, y5: 0 }]);
        setShowSavedCases(false);
        setActiveTab(0);
        setSaveMessage('Business Case loaded successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      setSaveMessage('Error loading Business Case');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleDelete = async (caseId) => {
    if (!window.confirm('Are you sure you want to delete this Business Case?')) return;
    try {
      await supabase.from('module_data').delete().eq('key', `bc_${caseId}`);
      const updatedList = savedCases.filter(c => c.id !== caseId);
      await supabase.from('module_data').upsert({ key: 'bc_list', content: updatedList }, { onConflict: 'key' });
      setSavedCases(updatedList);
      setSaveMessage('Business Case deleted');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Error deleting Business Case');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    setIsPrinting(true);
    
    try {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const element = document.getElementById('pl-content');
      const canvas = await window.html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new window.jspdf.jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 5;
      
      const scaledHeight = imgHeight * ratio;
      const pageHeight = pdfHeight - 10;
      let heightLeft = scaledHeight;
      let position = imgY;
      
      pdf.addImage(imgData, 'PNG', imgX, position, imgWidth * ratio, scaledHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft > 0) {
        position = heightLeft - scaledHeight + imgY;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', imgX, position, imgWidth * ratio, scaledHeight);
        heightLeft -= pageHeight;
      }
      
      const fileName = `BusinessCase_${projectInfo.name || 'Report'}_${projectInfo.country}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast('Error generating PDF. Please try again.', 'error');
    } finally {
      setIsPrinting(false);
      setIsGeneratingPDF(false);
    }
  };

  const addSku = () => {
    setSkus([...skus, {
      id: Date.now(),
      name: '',
      type: 'Private',
      unitsY1: '', unitsY2: '', unitsY3: '', unitsY4: '', unitsY5: '',
      valueY1: '', valueY2: '', valueY3: '', valueY4: '', valueY5: '',
      cagr: 0, aspLastMat: 0,
      growthY1: 0, growthY2: 0, growthY3: 0, growthY4: 0, growthY5: 0,
      msY1: 8, msY2: 13, msY3: 20, msY4: 25, msY5: 30,
      aspY1: '', aspY2: '', aspY3: '', aspY4: '', aspY5: '',
      aspPerUnit: '',
      aspAdjustY1: 5, aspAdjustY2: 5, aspAdjustY3: 5, aspAdjustY4: 5, aspAdjustY5: 5,
      cogsPerUnit: '', cogsInflation: 0,
      competitorsY1: '', competitorsY2: '', competitorsY3: '', competitorsY4: '', competitorsY5: ''
    }]);
  };

  const removeSku = (id) => {
    if (skus.length > 1) {
      setSkus(skus.filter(s => s.id !== id));
    }
  };

  const updateSku = (id, field, value) => {
    setSkus(skus.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const addExpense = () => {
    setExpenses([...expenses, {
      id: Date.now(),
      name: 'New Expense',
      type: 'USD',
      y0: 0, y1: 0, y2: 0, y3: 0, y4: 0, y5: 0
    }]);
  };

  const removeExpense = (id) => {
    setExpenses(expenses.filter(e => e.id !== id));
  };

  const updateExpense = (id, field, value) => {
    setExpenses(expenses.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const calculateCAGR = (sku) => {
    const mats = [
      { value: parseFloat(sku.unitsY1) || 0, years: 4 },
      { value: parseFloat(sku.unitsY2) || 0, years: 3 },
      { value: parseFloat(sku.unitsY3) || 0, years: 2 },
      { value: parseFloat(sku.unitsY4) || 0, years: 1 },
      { value: parseFloat(sku.unitsY5) || 0, years: 0 }
    ];
    const validMats = mats.filter(m => m.value > 0);
    if (validMats.length < 2) return 0;
    const oldest = validMats[0];
    const newest = validMats[validMats.length - 1];
    const yearsDiff = oldest.years - newest.years;
    if (yearsDiff === 0 || oldest.value === 0) return 0;
    return ((Math.pow(newest.value / oldest.value, 1/yearsDiff) - 1) * 100);
  };

  const calculateASPLastMat = (sku) => {
    const units = parseFloat(sku.unitsY5) || 0;
    const value = parseFloat(sku.valueY5) || 0;
    if (units > 0 && value > 0) return value / units;
    return 0;
  };

  const calculations = useMemo(() => {
    const results = skus.map(sku => {
      const cagr = calculateCAGR(sku);
      const aspLastMat = calculateASPLastMat(sku);
      const lastMatUnits = parseFloat(sku.unitsY5) || 0;

      let marketY1 = lastMatUnits * (1 + parseFloat(sku.growthY1 || 0) / 100);
      let marketY2 = marketY1 * (1 + parseFloat(sku.growthY2 || 0) / 100);
      let marketY3 = marketY2 * (1 + parseFloat(sku.growthY3 || 0) / 100);
      let marketY4 = marketY3 * (1 + parseFloat(sku.growthY4 || 0) / 100);
      let marketY5 = marketY4 * (1 + parseFloat(sku.growthY5 || 0) / 100);

      const unitsLuyeY1 = marketY1 * (parseFloat(sku.msY1 || 0) / 100);
      const unitsLuyeY2 = marketY2 * (parseFloat(sku.msY2 || 0) / 100);
      const unitsLuyeY3 = marketY3 * (parseFloat(sku.msY3 || 0) / 100);
      const unitsLuyeY4 = marketY4 * (parseFloat(sku.msY4 || 0) / 100);
      const unitsLuyeY5 = marketY5 * (parseFloat(sku.msY5 || 0) / 100);

      const aspBase = parseFloat(sku.aspPerUnit) || 0;

      let aspY1 = sku.aspY1 !== '' && sku.aspY1 !== null ? parseFloat(sku.aspY1) : aspBase;
      let aspY2 = sku.aspY2 !== '' && sku.aspY2 !== null ? parseFloat(sku.aspY2) : aspBase;
      let aspY3 = sku.aspY3 !== '' && sku.aspY3 !== null ? parseFloat(sku.aspY3) : aspBase;
      let aspY4 = sku.aspY4 !== '' && sku.aspY4 !== null ? parseFloat(sku.aspY4) : aspBase;
      let aspY5 = sku.aspY5 !== '' && sku.aspY5 !== null ? parseFloat(sku.aspY5) : aspBase;

      const salesY1 = unitsLuyeY1 * aspY1;
      const salesY2 = unitsLuyeY2 * aspY2;
      const salesY3 = unitsLuyeY3 * aspY3;
      const salesY4 = unitsLuyeY4 * aspY4;
      const salesY5 = unitsLuyeY5 * aspY5;

      const cogsBase = parseFloat(sku.cogsPerUnit) || 0;
      // Per-year COGS change %, compounding on the previous year's cost. Negative
      // values mean savings. Old saved cases (single cogsInflation) keep working:
      // undefined per-year fields fall back to the legacy value (Y2+ only, as before).
      const legacyInf = parseFloat(sku.cogsInflation || 0);
      const inflFor = (key, fallback) => { const v = sku[key]; if (v === undefined || v === null) return fallback; return parseFloat(v) || 0; };
      const cogsY1 = cogsBase * (1 + inflFor('cogsInflY1', 0) / 100);
      const cogsY2 = cogsY1 * (1 + inflFor('cogsInflY2', legacyInf) / 100);
      const cogsY3 = cogsY2 * (1 + inflFor('cogsInflY3', legacyInf) / 100);
      const cogsY4 = cogsY3 * (1 + inflFor('cogsInflY4', legacyInf) / 100);
      const cogsY5 = cogsY4 * (1 + inflFor('cogsInflY5', legacyInf) / 100);

      const totalCogsY1 = unitsLuyeY1 * cogsY1;
      const totalCogsY2 = unitsLuyeY2 * cogsY2;
      const totalCogsY3 = unitsLuyeY3 * cogsY3;
      const totalCogsY4 = unitsLuyeY4 * cogsY4;
      const totalCogsY5 = unitsLuyeY5 * cogsY5;

      return {
        sku,
        cagr,
        aspLastMat,
        aspBase,
        marketUnits: { y1: marketY1, y2: marketY2, y3: marketY3, y4: marketY4, y5: marketY5 },
        unitsLuye: { y1: unitsLuyeY1, y2: unitsLuyeY2, y3: unitsLuyeY3, y4: unitsLuyeY4, y5: unitsLuyeY5 },
        asp: { y1: aspY1, y2: aspY2, y3: aspY3, y4: aspY4, y5: aspY5 },
        sales: { y1: salesY1, y2: salesY2, y3: salesY3, y4: salesY4, y5: salesY5 },
        cogs: { y1: cogsY1, y2: cogsY2, y3: cogsY3, y4: cogsY4, y5: cogsY5 },
        totalCogs: { y1: totalCogsY1, y2: totalCogsY2, y3: totalCogsY3, y4: totalCogsY4, y5: totalCogsY5 },
        gp: { y1: salesY1 - totalCogsY1, y2: salesY2 - totalCogsY2, y3: salesY3 - totalCogsY3, y4: salesY4 - totalCogsY4, y5: salesY5 - totalCogsY5 }
      };
    });

    const privateResults = results.filter(r => r.sku.type === 'Private');
    const publicResults = results.filter(r => r.sku.type === 'Public');
    const hybridResults = results.filter(r => r.sku.type === 'Hybrid');

    const sumField = (arr, field) => ({
      y1: arr.reduce((sum, r) => sum + r[field].y1, 0),
      y2: arr.reduce((sum, r) => sum + r[field].y2, 0),
      y3: arr.reduce((sum, r) => sum + r[field].y3, 0),
      y4: arr.reduce((sum, r) => sum + r[field].y4, 0),
      y5: arr.reduce((sum, r) => sum + r[field].y5, 0)
    });

    const privateTotals = { units: sumField(privateResults, 'unitsLuye'), sales: sumField(privateResults, 'sales'), totalCogs: sumField(privateResults, 'totalCogs'), gp: sumField(privateResults, 'gp') };
    const publicTotals = { units: sumField(publicResults, 'unitsLuye'), sales: sumField(publicResults, 'sales'), totalCogs: sumField(publicResults, 'totalCogs'), gp: sumField(publicResults, 'gp') };
    // Hybrid (private + government mix) sells direct like Public: no partner margin deduction.
    const hybridTotals = { units: sumField(hybridResults, 'unitsLuye'), sales: sumField(hybridResults, 'sales'), totalCogs: sumField(hybridResults, 'totalCogs'), gp: sumField(hybridResults, 'gp') };

    // Partner Margin from COGS expenses (only when With Partner model)
    const partnerMarginExp = projectInfo.businessModel === 'partner' ? expenses.find(e => e.name === 'Partner Margin' && e.type === '%') : null;
    const partnerMarginPct = {
      y1: partnerMarginExp ? (parseFloat(partnerMarginExp.y1) || 0) : 0,
      y2: partnerMarginExp ? (parseFloat(partnerMarginExp.y2) || 0) : 0,
      y3: partnerMarginExp ? (parseFloat(partnerMarginExp.y3) || 0) : 0,
      y4: partnerMarginExp ? (parseFloat(partnerMarginExp.y4) || 0) : 0,
      y5: partnerMarginExp ? (parseFloat(partnerMarginExp.y5) || 0) : 0
    };
    const privatePartnerMargin = {
      y1: privateTotals.sales.y1 * partnerMarginPct.y1 / 100,
      y2: privateTotals.sales.y2 * partnerMarginPct.y2 / 100,
      y3: privateTotals.sales.y3 * partnerMarginPct.y3 / 100,
      y4: privateTotals.sales.y4 * partnerMarginPct.y4 / 100,
      y5: privateTotals.sales.y5 * partnerMarginPct.y5 / 100
    };
    const privateLuyeSales = {
      y1: privateTotals.sales.y1 - privatePartnerMargin.y1,
      y2: privateTotals.sales.y2 - privatePartnerMargin.y2,
      y3: privateTotals.sales.y3 - privatePartnerMargin.y3,
      y4: privateTotals.sales.y4 - privatePartnerMargin.y4,
      y5: privateTotals.sales.y5 - privatePartnerMargin.y5
    };
    const privateLuyeGP = {
      y1: privateLuyeSales.y1 - privateTotals.totalCogs.y1,
      y2: privateLuyeSales.y2 - privateTotals.totalCogs.y2,
      y3: privateLuyeSales.y3 - privateTotals.totalCogs.y3,
      y4: privateLuyeSales.y4 - privateTotals.totalCogs.y4,
      y5: privateLuyeSales.y5 - privateTotals.totalCogs.y5
    };

    const sumYears = (a, b, c) => ({ y1: a.y1 + b.y1 + c.y1, y2: a.y2 + b.y2 + c.y2, y3: a.y3 + b.y3 + c.y3, y4: a.y4 + b.y4 + c.y4, y5: a.y5 + b.y5 + c.y5 });
    const grandTotals = {
      units: sumYears(privateTotals.units, publicTotals.units, hybridTotals.units),
      sales: sumYears(privateTotals.sales, publicTotals.sales, hybridTotals.sales),
      totalCogs: sumYears(privateTotals.totalCogs, publicTotals.totalCogs, hybridTotals.totalCogs),
      gp: sumYears(privateTotals.gp, publicTotals.gp, hybridTotals.gp)
    };

    // Total Luye Sales = private Luye Sales (after partner margin) + public + hybrid Sales (direct)
    const totalLuyeSales = {
      y1: privateLuyeSales.y1 + publicTotals.sales.y1 + hybridTotals.sales.y1,
      y2: privateLuyeSales.y2 + publicTotals.sales.y2 + hybridTotals.sales.y2,
      y3: privateLuyeSales.y3 + publicTotals.sales.y3 + hybridTotals.sales.y3,
      y4: privateLuyeSales.y4 + publicTotals.sales.y4 + hybridTotals.sales.y4,
      y5: privateLuyeSales.y5 + publicTotals.sales.y5 + hybridTotals.sales.y5
    };
    const totalLuyeGP = {
      y1: totalLuyeSales.y1 - grandTotals.totalCogs.y1,
      y2: totalLuyeSales.y2 - grandTotals.totalCogs.y2,
      y3: totalLuyeSales.y3 - grandTotals.totalCogs.y3,
      y4: totalLuyeSales.y4 - grandTotals.totalCogs.y4,
      y5: totalLuyeSales.y5 - grandTotals.totalCogs.y5
    };

    // Market totals (sum of all SKU market units)
    const marketTotals = {
      y1: results.reduce((sum, r) => sum + r.marketUnits.y1, 0),
      y2: results.reduce((sum, r) => sum + r.marketUnits.y2, 0),
      y3: results.reduce((sum, r) => sum + r.marketUnits.y3, 0),
      y4: results.reduce((sum, r) => sum + r.marketUnits.y4, 0),
      y5: results.reduce((sum, r) => sum + r.marketUnits.y5, 0)
    };

    const totalExpensesUSD = { y0: 0, y1: 0, y2: 0, y3: 0, y4: 0, y5: 0 };
    const totalExpensesPct = { y0: 0, y1: 0, y2: 0, y3: 0, y4: 0, y5: 0 };
    const totalExpensesPctAsUSD = { y0: 0, y1: 0, y2: 0, y3: 0, y4: 0, y5: 0 };
    expenses.forEach(exp => {
      ['y0', 'y1', 'y2', 'y3', 'y4', 'y5'].forEach(year => {
        const val = parseFloat(exp[year]) || 0;
        if (exp.type === '%') {
          const sales = grandTotals.sales[year] || 0;
          totalExpensesPct[year] += val;
          totalExpensesPctAsUSD[year] += (sales * val / 100);
        } else {
          totalExpensesUSD[year] += val;
        }
      });
    });
    const totalExpenses = {
      y0: totalExpensesUSD.y0 + totalExpensesPctAsUSD.y0,
      y1: totalExpensesUSD.y1 + totalExpensesPctAsUSD.y1,
      y2: totalExpensesUSD.y2 + totalExpensesPctAsUSD.y2,
      y3: totalExpensesUSD.y3 + totalExpensesPctAsUSD.y3,
      y4: totalExpensesUSD.y4 + totalExpensesPctAsUSD.y4,
      y5: totalExpensesUSD.y5 + totalExpensesPctAsUSD.y5
    };

    const upfrontTotals = {
      y0: upfrontPayments.reduce((sum, u) => sum + (parseFloat(u.y0) || 0), 0),
      y1: upfrontPayments.reduce((sum, u) => sum + (parseFloat(u.y1) || 0), 0),
      y2: upfrontPayments.reduce((sum, u) => sum + (parseFloat(u.y2) || 0), 0),
      y3: upfrontPayments.reduce((sum, u) => sum + (parseFloat(u.y3) || 0), 0),
      y4: upfrontPayments.reduce((sum, u) => sum + (parseFloat(u.y4) || 0), 0),
      y5: upfrontPayments.reduce((sum, u) => sum + (parseFloat(u.y5) || 0), 0)
    };

    const ebitda = {
      y0: upfrontTotals.y0 - totalExpenses.y0,
      y1: totalLuyeGP.y1 + upfrontTotals.y1 - totalExpenses.y1,
      y2: totalLuyeGP.y2 + upfrontTotals.y2 - totalExpenses.y2,
      y3: totalLuyeGP.y3 + upfrontTotals.y3 - totalExpenses.y3,
      y4: totalLuyeGP.y4 + upfrontTotals.y4 - totalExpenses.y4,
      y5: totalLuyeGP.y5 + upfrontTotals.y5 - totalExpenses.y5
    };

    const accum5yr = {
      sales: totalLuyeSales.y1 + totalLuyeSales.y2 + totalLuyeSales.y3 + totalLuyeSales.y4 + totalLuyeSales.y5,
      cogs: grandTotals.totalCogs.y1 + grandTotals.totalCogs.y2 + grandTotals.totalCogs.y3 + grandTotals.totalCogs.y4 + grandTotals.totalCogs.y5,
      gp: totalLuyeGP.y1 + totalLuyeGP.y2 + totalLuyeGP.y3 + totalLuyeGP.y4 + totalLuyeGP.y5,
      expenses: totalExpenses.y1 + totalExpenses.y2 + totalExpenses.y3 + totalExpenses.y4 + totalExpenses.y5,
      upfront: upfrontTotals.y0 + upfrontTotals.y1 + upfrontTotals.y2 + upfrontTotals.y3 + upfrontTotals.y4 + upfrontTotals.y5,
      ebitda: ebitda.y0 + ebitda.y1 + ebitda.y2 + ebitda.y3 + ebitda.y4 + ebitda.y5
    };

    // NPV calculation using WACC
    const waccRate = (parseFloat(projectInfo.wacc) || 0) / 100;
    const npvByYear = {
      y0: ebitda.y0,
      y1: ebitda.y1 / Math.pow(1 + waccRate, 1),
      y2: ebitda.y2 / Math.pow(1 + waccRate, 2),
      y3: ebitda.y3 / Math.pow(1 + waccRate, 3),
      y4: ebitda.y4 / Math.pow(1 + waccRate, 4),
      y5: ebitda.y5 / Math.pow(1 + waccRate, 5)
    };
    const npvTotal = npvByYear.y0 + npvByYear.y1 + npvByYear.y2 + npvByYear.y3 + npvByYear.y4 + npvByYear.y5;

    return { bysku: results, privateResults, publicResults, hybridResults, privateTotals, publicTotals, hybridTotals, privatePartnerMargin, partnerMarginPct, privateLuyeSales, privateLuyeGP, totalLuyeSales, totalLuyeGP, grandTotals, marketTotals, totalExpenses, totalExpensesUSD, totalExpensesPct, totalExpensesPctAsUSD, ebitda, accum5yr, upfrontTotals, npvByYear, npvTotal };
  }, [skus, expenses, upfrontPayments, projectInfo.businessModel, projectInfo.wacc]);

  const tabs = [
    { name: 'Market Data', icon: FileText, desc: 'Project info & market history' },
    { name: 'Luye Strategy', icon: TrendingUp, desc: 'Price, share & upfronts' },
    { name: 'Costs', icon: DollarSign, desc: 'COGS & expenses' },
    { name: 'Results (P&L)', icon: PieChart, desc: 'Auto-calculated' }
  ];

  // What to do on each step — shown as a guidance banner under the tab bar.
  const stepGuides = [
    { title: 'Step 1 · Market Data', text: 'Describe the project, then enter each SKU’s historical market size: units (and optionally values) for the last 5 MAT periods, oldest (Y-4) to most recent (Y0). Finally, set the expected market growth % per year. CAGR and ASP are calculated for you.' },
    { title: 'Step 2 · Luye Strategy', text: 'Now define Luye’s plan: the selling price (ASP) per SKU, your target market share % for each projection year, and any upfront payments from partners. The competitors table is reference-only.' },
    { title: 'Step 3 · Costs', text: 'Enter each SKU’s COGS per unit (with annual inflation %) and the operating expenses. "USD" expenses are fixed amounts; "%" expenses are calculated over total sales.' },
    { title: 'Step 4 · Results', text: 'Nothing to type here — every figure is calculated from steps 1–3. Review the KPIs, the chart and the P&L tables, then export to PDF.' }
  ];

  // Soft completion signals per step (used by the wizard progress checkmarks).
  const historicalPeriods = (sku) => ['unitsY1', 'unitsY2', 'unitsY3', 'unitsY4', 'unitsY5'].filter(k => parseFloat(sku[k]) > 0).length;
  const stepDone = [
    skus.some(s => historicalPeriods(s) >= 2),
    skus.some(s => parseFloat(s.aspPerUnit) > 0 || ['aspY1', 'aspY2', 'aspY3', 'aspY4', 'aspY5'].some(k => parseFloat(s[k]) > 0)) && skus.some(s => ['msY1', 'msY2', 'msY3', 'msY4', 'msY5'].some(k => parseFloat(s[k]) > 0)),
    skus.some(s => parseFloat(s.cogsPerUnit) > 0),
    false
  ];

  const formatCurrency = (num) => {
    if (isNaN(num) || num === null) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(num);
  };

  const formatNumber = (num) => {
    if (isNaN(num) || num === null) return '0';
    return Math.round(num).toLocaleString('en-US');
  };

  const formatNumberInput = (value) => {
    if (!value) return '';
    const num = value.toString().replace(/,/g, '');
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const cleanNumberInput = (value) => {
    if (!value) return '';
    return value.toString().replace(/,/g, '').replace(/\$/g, '');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Business Case</h2>
          <p className="text-sm text-slate-500">Luye Pharma LATAM Financial Evaluation</p>
        </div>
        <div className="flex items-center gap-3">
          {saveMessage && (
            <span className={`px-3 py-1 rounded-lg text-sm ${saveMessage.includes('Error') || saveMessage.includes('Please') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
              {saveMessage}
            </span>
          )}
          <button onClick={() => setShowSavedCases(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-md">
            <FolderOpen className="w-5 h-5" />Open
          </button>
          <button onClick={handleSave} disabled={isSaving} className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-md ${isSaving ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}>
            <Save className="w-5 h-5" />{isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Modal for saved cases */}
      {showSavedCases && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-96 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b bg-slate-50">
              <h2 className="text-xl font-bold text-slate-800">Saved Business Cases</h2>
              <button onClick={() => setShowSavedCases(false)} className="text-slate-500 hover:text-slate-700"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-72">
              {savedCases.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No saved Business Cases yet</p>
              ) : (
                <div className="space-y-2">
                  {savedCases.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800">{item.name}</h3>
                        <p className="text-sm text-slate-500">{item.country} • {item.product || 'No product'} • {new Date(item.savedAt).toLocaleDateString()}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleLoad(item.id)} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Load</button>
                        <button onClick={() => handleDelete(item.id)} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-slate-200">
        {/* Stepper rail: numbered nodes joined by a progress line */}
        <div className="px-10 pt-8 pb-2">
          <div className="flex items-start">
            {tabs.map((tab, idx) => {
              const Icon = tab.icon;
              const active = activeTab === idx;
              const done = stepDone[idx] && !active;
              return (
                <Fragment key={idx}>
                  {idx > 0 && <div className={`flex-1 h-1 rounded-full mt-[22px] mx-3 transition-colors ${stepDone[idx - 1] ? 'bg-green-400' : 'bg-slate-200'}`} />}
                  <button onClick={() => setActiveTab(idx)} className="flex flex-col items-center gap-2 group shrink-0">
                    <span className={`flex items-center justify-center w-12 h-12 rounded-full border-2 text-base font-bold transition-all ${active ? 'bg-blue-600 border-blue-600 text-white shadow-lg ring-4 ring-blue-100 scale-110' : done ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-slate-300 text-slate-400 group-hover:border-blue-400 group-hover:text-blue-500'}`}>
                      {done ? <CheckCircle2 className="w-6 h-6" /> : idx + 1}
                    </span>
                    <span className="text-center">
                      <span className={`flex items-center justify-center gap-1 text-sm font-semibold whitespace-nowrap ${active ? 'text-blue-700' : done ? 'text-green-600' : 'text-slate-400'}`}><Icon className="w-3.5 h-3.5" />{tab.name}</span>
                      <span className={`block text-[11px] ${active ? 'text-slate-500' : 'text-slate-300'}`}>{tab.desc}</span>
                    </span>
                  </button>
                </Fragment>
              );
            })}
          </div>
        </div>

        {/* Step header: number-in-context + what to do here + input/output legend */}
        <div className="px-6 pt-4">
          <div className="border-l-4 border-blue-600 bg-gradient-to-r from-blue-50 to-white rounded-r-lg p-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-64">
              <p className="text-[11px] font-bold tracking-widest text-blue-500 uppercase">Step {activeTab + 1} of {tabs.length}</p>
              <h3 className="text-lg font-bold text-slate-800">{tabs[activeTab].name}</h3>
              <p className="text-sm text-slate-600 mt-1 max-w-4xl">{stepGuides[activeTab].text}</p>
            </div>
            {activeTab < 3 && (
              <span className="text-xs text-slate-500 whitespace-nowrap self-center">
                <span className="inline-block w-3 h-3 bg-white border border-slate-300 rounded align-middle mr-1"></span>you enter
                <span className="inline-block w-3 h-3 bg-blue-100 border border-blue-200 rounded align-middle ml-3 mr-1"></span>auto-calculated
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 0 && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-700">
                  <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/15"><Briefcase className="w-5 h-5 text-white" /></span>
                  <div>
                    <h3 className="font-bold text-white">Project Information</h3>
                    <p className="text-xs text-slate-300">Identify the case and how Luye will go to market</p>
                  </div>
                </div>
                <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Left: what is this case about */}
                  <div className="space-y-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <p className="text-[11px] font-bold tracking-widest text-indigo-700 uppercase flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> About the case</p>
                    {/* Live case preview — builds up as the fields are typed */}
                    {(() => {
                      const FLAGS = { mexico: '🇲🇽', brazil: '🇧🇷', brasil: '🇧🇷', chile: '🇨🇱', colombia: '🇨🇴', peru: '🇵🇪', perú: '🇵🇪', argentina: '🇦🇷', uruguay: '🇺🇾', ecuador: '🇪🇨', panama: '🇵🇦', panamá: '🇵🇦', guatemala: '🇬🇹', 'costa rica': '🇨🇷' };
                      const flag = FLAGS[(projectInfo.country || '').trim().toLowerCase()] || '🌎';
                      return (
                        <div className="rounded-xl border-2 border-dashed border-indigo-300 bg-white p-4">
                          <p className="text-lg font-bold text-slate-800 leading-tight">{projectInfo.name || <span className="text-slate-300">Untitled case</span>}</p>
                          <div className="flex items-center gap-2 flex-wrap mt-2">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${projectInfo.country ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-400'}`}>{flag} {projectInfo.country || 'Country'}</span>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${projectInfo.product ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-400'}`}><Package className="w-3 h-3" /> {projectInfo.product || 'Product'}</span>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-800"><Calendar className="w-3 h-3" /> Base {projectInfo.year}</span>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${projectInfo.businessModel === 'partner' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>{projectInfo.businessModel === 'partner' ? <Users className="w-3 h-3" /> : <Building2 className="w-3 h-3" />} {projectInfo.businessModel === 'partner' ? (projectInfo.partner || 'With Partner') : 'Luye Direct'}</span>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="relative">
                      <Briefcase className="w-4 h-4 text-indigo-400 absolute left-3 top-[38px]" />
                      <label className="block text-sm font-semibold text-indigo-900 mb-1">Project Name</label>
                      <input type="text" value={projectInfo.name} onChange={(e) => setProjectInfo({...projectInfo, name: e.target.value})} className="w-full pl-9 pr-3 py-2.5 bg-white border-2 border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200" placeholder="e.g., Paliperidone Mexico" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="relative">
                        <Globe className="w-4 h-4 text-indigo-400 absolute left-3 top-[38px]" />
                        <label className="block text-sm font-semibold text-indigo-900 mb-1">Country</label>
                        <input type="text" value={projectInfo.country} onChange={(e) => setProjectInfo({...projectInfo, country: e.target.value})} className="w-full pl-9 pr-3 py-2.5 bg-white border-2 border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200" placeholder="Mexico" />
                      </div>
                      <div className="relative">
                        <Package className="w-4 h-4 text-indigo-400 absolute left-3 top-[38px]" />
                        <label className="block text-sm font-semibold text-indigo-900 mb-1">Product</label>
                        <input type="text" value={projectInfo.product} onChange={(e) => setProjectInfo({...projectInfo, product: e.target.value})} className="w-full pl-9 pr-3 py-2.5 bg-white border-2 border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200" placeholder="Paliperidone" />
                      </div>
                      <div className="relative">
                        <Calendar className="w-4 h-4 text-indigo-400 absolute left-3 top-[38px]" />
                        <label className="block text-sm font-semibold text-indigo-900 mb-1">Base Year</label>
                        <input type="text" value={projectInfo.year} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d+$/.test(val)) setProjectInfo({...projectInfo, year: val === '' ? new Date().getFullYear() : parseInt(val)}); }} className="w-full pl-9 pr-3 py-2.5 bg-white border-2 border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200" />
                      </div>
                    </div>
                  </div>
                  {/* Right: commercial model */}
                  <div className="space-y-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-[11px] font-bold tracking-widest text-amber-700 uppercase flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Commercial model</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" onClick={() => setProjectInfo({...projectInfo, businessModel: 'partner'})} className={`text-left border-2 rounded-xl p-3 transition-all ${projectInfo.businessModel === 'partner' ? 'border-orange-400 bg-orange-50 shadow-md' : 'border-slate-200 bg-white hover:border-orange-300'}`}>
                        <span className={`flex items-center justify-center w-8 h-8 rounded-lg mb-2 ${projectInfo.businessModel === 'partner' ? 'bg-orange-100' : 'bg-slate-100'}`}><Users className={`w-4 h-4 ${projectInfo.businessModel === 'partner' ? 'text-orange-600' : 'text-slate-400'}`} /></span>
                        <span className={`block text-sm font-bold ${projectInfo.businessModel === 'partner' ? 'text-orange-800' : 'text-slate-600'}`}>With Partner</span>
                        <span className="block text-[11px] text-slate-500 mt-0.5">Sales flow through a local partner — partner margin applies</span>
                      </button>
                      <button type="button" onClick={() => setProjectInfo({...projectInfo, businessModel: 'direct'})} className={`text-left border-2 rounded-xl p-3 transition-all ${projectInfo.businessModel === 'direct' ? 'border-blue-400 bg-blue-50 shadow-md' : 'border-slate-200 bg-white hover:border-blue-300'}`}>
                        <span className={`flex items-center justify-center w-8 h-8 rounded-lg mb-2 ${projectInfo.businessModel === 'direct' ? 'bg-blue-100' : 'bg-slate-100'}`}><Building2 className={`w-4 h-4 ${projectInfo.businessModel === 'direct' ? 'text-blue-600' : 'text-slate-400'}`} /></span>
                        <span className={`block text-sm font-bold ${projectInfo.businessModel === 'direct' ? 'text-blue-800' : 'text-slate-600'}`}>Luye Direct</span>
                        <span className="block text-[11px] text-slate-500 mt-0.5">Luye sells directly — no partner margin</span>
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <Users className="w-4 h-4 text-amber-500 absolute left-3 top-[38px]" />
                        <label className="block text-sm font-semibold text-amber-900 mb-1">Partner</label>
                        <input type="text" value={projectInfo.partner} onChange={(e) => setProjectInfo({...projectInfo, partner: e.target.value})} className={`w-full pl-9 pr-3 py-2.5 border-2 rounded-lg focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 ${projectInfo.businessModel === 'direct' ? 'bg-slate-100 text-slate-400 border-slate-200' : 'bg-white border-amber-200'}`} placeholder={projectInfo.businessModel === 'direct' ? 'N/A' : 'e.g., Partner Name'} disabled={projectInfo.businessModel === 'direct'} />
                      </div>
                      <div className="relative">
                        <Percent className="w-4 h-4 text-purple-500 absolute left-3 top-[38px]" />
                        <label className="block text-sm font-semibold text-purple-900 mb-1">WACC % <span className="text-purple-400 font-normal cursor-help" title="Weighted Average Cost of Capital — ask your Finance department. Used to discount future cash flows for the NPV. Leave at 0 to skip discounting.">ⓘ</span></label>
                        <input type="text" value={projectInfo.wacc} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d*\.?\d*$/.test(val)) setProjectInfo({...projectInfo, wacc: val === '' ? 0 : parseFloat(val) || 0}); }} className="w-full pl-9 pr-3 py-2.5 border-2 border-purple-300 bg-white rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 font-semibold text-purple-800" placeholder="e.g., 10" />
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-400">WACC is provided by Finance; with 0 the NPV equals the accumulated EBITDA.</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-slate-800">SKU Historical Data</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Total market size per SKU (not just Luye). MAT = Moving Annual Total, the rolling 12-month figure. Y-4 is the oldest period; Base Year is the most recent.</p>
                  </div>
                  <button onClick={addSku} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"><Plus className="w-4 h-4" />Add SKU</button>
                </div>
                <div className="space-y-3">
                  {skus.map(sku => {
                    const calc = calculations.bysku.find(c => c.sku.id === sku.id);
                    const typePill = { Private: 'bg-orange-50 text-orange-700 border-orange-200', Public: 'bg-sky-50 text-sky-700 border-sky-200', Hybrid: 'bg-violet-50 text-violet-700 border-violet-200' }[sku.type] || 'bg-slate-50 text-slate-600 border-slate-200';
                    const yearTags = ['Y-4', 'Y-3', 'Y-2', 'Y-1', 'Base'];
                    return (
                      <div key={sku.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 flex-wrap mb-4">
                          <input type="text" value={sku.name} onChange={(e) => updateSku(sku.id, 'name', e.target.value)} className="flex-1 min-w-44 px-1 py-1 text-base font-bold text-slate-800 bg-transparent border-b-2 border-transparent hover:border-slate-200 focus:border-blue-400 focus:outline-none" placeholder="SKU name — e.g., 75MG/1ML" />
                          <select value={sku.type} onChange={(e) => updateSku(sku.id, 'type', e.target.value)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border cursor-pointer ${typePill}`}>
                            <option value="Private">Private</option><option value="Public">Public</option><option value="Hybrid">Hybrid</option>
                          </select>
                          <span title="Compound Annual Growth Rate — auto-calculated from the historical units (needs at least 2 periods)" className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border cursor-help ${calc && calc.cagr < 0 ? 'bg-red-50 text-red-700 border-red-100' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>
                            <TrendingUp className="w-3 h-3" /> CAGR {calc ? `${calc.cagr >= 0 ? '+' : ''}${calc.cagr.toFixed(1)}` : '0.0'}%
                          </span>
                          <span title="Average Selling Price of the most recent year — auto-calculated as Values ÷ Units of the Base Year" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-100 cursor-help">
                            <DollarSign className="w-3 h-3" /> ASP {calc ? formatCurrency(calc.aspLastMat) : '$0'}
                          </span>
                          <button onClick={() => removeSku(sku.id)} disabled={skus.length === 1} className="p-1.5 rounded-lg bg-red-50 text-red-500 border border-red-200 hover:bg-red-500 hover:text-white disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-[11px] font-bold tracking-widest text-blue-700 uppercase mb-2 flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> Market units · MAT</p>
                            <div className="grid grid-cols-5 gap-2">
                              {['unitsY1', 'unitsY2', 'unitsY3', 'unitsY4', 'unitsY5'].map((field, i) => (
                                <div key={field}>
                                  <label className="block text-[11px] font-bold text-blue-600 text-center mb-1">{yearTags[i]}</label>
                                  <input type="text" value={sku[field] ? formatNumberInput(sku[field]) : ''} onChange={(e) => { const cleaned = cleanNumberInput(e.target.value); if (cleaned === '' || /^\d+$/.test(cleaned)) updateSku(sku.id, field, cleaned); }} className="w-full px-2 py-2 bg-white border-2 border-blue-200 rounded-lg text-right text-sm font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none" placeholder="0" />
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                            <p className="text-[11px] font-bold tracking-widest text-emerald-700 uppercase mb-2 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Market values · USD <span className="normal-case font-normal text-emerald-600">(optional, enables ASP)</span></p>
                            <div className="grid grid-cols-5 gap-2">
                              {['valueY1', 'valueY2', 'valueY3', 'valueY4', 'valueY5'].map((field, i) => (
                                <div key={field}>
                                  <label className="block text-[11px] font-bold text-emerald-600 text-center mb-1">{yearTags[i]}</label>
                                  <input type="text" value={sku[field] ? '$' + formatNumberInput(sku[field]) : ''} onChange={(e) => { const cleaned = cleanNumberInput(e.target.value); if (cleaned === '' || /^\d*\.?\d*$/.test(cleaned)) updateSku(sku.id, field, cleaned); }} className="w-full px-2 py-2 bg-white border-2 border-emerald-200 rounded-lg text-right text-sm font-medium focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none" placeholder="$0" />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-2">* Fill at least 2 unit periods for CAGR. Values are optional — when provided, the ASP chip is calculated automatically.</p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-[11px] font-bold tracking-widest text-yellow-700 uppercase flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Market Growth Projection % · YoY</p>
                <p className="text-xs text-yellow-800/70 mt-0.5 mb-3">Expected growth of the whole market. Each year builds on the previous year's market size.</p>
                <div className="space-y-2">
                  <div className="grid grid-cols-6 gap-2">
                    <span></span>
                    {['Y1', 'Y2', 'Y3', 'Y4', 'Y5'].map(y => <span key={y} className="text-[11px] font-bold text-yellow-700 text-center">{y} %</span>)}
                  </div>
                  {skus.map(sku => (
                    <div key={sku.id} className="grid grid-cols-6 gap-2 items-center">
                      <span className="font-semibold text-slate-700 text-sm truncate pr-2" title={sku.name}>{sku.name || 'Unnamed SKU'}</span>
                      {['growthY1', 'growthY2', 'growthY3', 'growthY4', 'growthY5'].map(field => (
                        <input key={field} type="text" value={sku[field]} onChange={(e) => { const val = e.target.value; if (val === '' || val === '-' || /^-?\d*\.?\d*$/.test(val)) updateSku(sku.id, field, val === '' ? 0 : parseFloat(val) || 0); }} className="w-full px-2 py-2 bg-white border-2 border-yellow-300 rounded-lg text-center text-sm font-medium focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none" />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div className="space-y-6">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-[11px] font-bold tracking-widest text-emerald-700 uppercase flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> ASP — Average Selling Price per SKU</p>
                <p className="text-xs text-emerald-800/70 mt-0.5 mb-3">Enter the base price; years left empty use it automatically. Type in a year only to override it.</p>
                <div className="space-y-2">
                  <div className="grid grid-cols-7 gap-2">
                    <span></span>
                    <span className="text-[11px] font-bold text-emerald-800 text-center">Base ASP (USD)</span>
                    {['Y1', 'Y2', 'Y3', 'Y4', 'Y5'].map(y => <span key={y} className="text-[11px] font-bold text-emerald-700 text-center">{y} <span className="font-normal">(override)</span></span>)}
                  </div>
                  {skus.map(sku => {
                    const calc = calculations.bysku.find(c => c.sku.id === sku.id);
                    return (
                      <div key={sku.id} className="grid grid-cols-7 gap-2 items-center">
                        <span className="font-semibold text-slate-700 text-sm truncate pr-2" title={sku.name}>{sku.name || 'Unnamed SKU'}</span>
                        <input type="text" value={sku.aspPerUnit !== '' && sku.aspPerUnit !== undefined ? '$' + sku.aspPerUnit : ''} onChange={(e) => { const cleaned = e.target.value.replace(/\$/g, '').replace(/,/g, ''); if (cleaned === '' || /^\d*\.?\d{0,2}$/.test(cleaned)) updateSku(sku.id, 'aspPerUnit', cleaned); }} className="w-full px-2 py-2 bg-white border-2 border-emerald-400 rounded-lg text-center text-sm font-bold text-emerald-800 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 focus:outline-none" placeholder="$0.00" />
                        {['aspY1', 'aspY2', 'aspY3', 'aspY4', 'aspY5'].map((field) => (
                          <input key={field} type="text" value={sku[field] !== '' && sku[field] !== null ? sku[field] : ''} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d*\.?\d*$/.test(val)) updateSku(sku.id, field, val); }} className="w-full px-2 py-2 bg-white border-2 border-emerald-200 rounded-lg text-center text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none placeholder:text-emerald-300" placeholder={calc && calc.aspBase ? `$${calc.aspBase.toFixed(2)}` : '$0'} />
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-slate-100 border border-slate-200 rounded-xl p-4">
                <p className="text-[11px] font-bold tracking-widest text-slate-500 uppercase flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Expected Competitors <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 normal-case font-semibold">reference only</span></p>
                <p className="text-xs text-slate-500 mt-0.5 mb-3">Does not affect any calculation — use it to justify your ASP and share assumptions (price erosion).</p>
                <div className="space-y-2">
                  <div className="grid grid-cols-6 gap-2">
                    <span></span>
                    {['Y1', 'Y2', 'Y3', 'Y4', 'Y5'].map(y => <span key={y} className="text-[11px] font-bold text-slate-500 text-center">{y}</span>)}
                  </div>
                  {skus.map(sku => (
                    <div key={sku.id} className="grid grid-cols-6 gap-2 items-center">
                      <span className="font-semibold text-slate-700 text-sm truncate pr-2" title={sku.name}>{sku.name || 'Unnamed SKU'}</span>
                      {['competitorsY1', 'competitorsY2', 'competitorsY3', 'competitorsY4', 'competitorsY5'].map(field => (
                        <input key={field} type="text" value={sku[field]} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d+$/.test(val)) updateSku(sku.id, field, val); }} className="w-full px-2 py-2 bg-white border-2 border-slate-300 rounded-lg text-center text-sm focus:border-slate-500 focus:ring-2 focus:ring-slate-200 focus:outline-none" placeholder="0" />
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                <p className="text-[11px] font-bold tracking-widest text-violet-700 uppercase flex items-center gap-1.5"><Percent className="w-3.5 h-3.5" /> Luye Market Share Projection %</p>
                <p className="text-xs text-violet-800/70 mt-0.5 mb-3">Your target share of the market per year — this drives Luye units (share % × market units).</p>
                <div className="space-y-2">
                  <div className="grid grid-cols-6 gap-2">
                    <span></span>
                    {['Y1', 'Y2', 'Y3', 'Y4', 'Y5'].map(y => <span key={y} className="text-[11px] font-bold text-violet-700 text-center">{y} %</span>)}
                  </div>
                  {skus.map(sku => (
                    <div key={sku.id} className="grid grid-cols-6 gap-2 items-center">
                      <span className="font-semibold text-slate-700 text-sm truncate pr-2" title={sku.name}>{sku.name || 'Unnamed SKU'}</span>
                      {['msY1', 'msY2', 'msY3', 'msY4', 'msY5'].map(field => (
                        <input key={field} type="text" value={sku[field]} onChange={(e) => { const val = e.target.value; if (val === '' || /^\d*\.?\d*$/.test(val)) updateSku(sku.id, field, val === '' ? 0 : parseFloat(val) || 0); }} className="w-full px-2 py-2 bg-white border-2 border-violet-300 rounded-lg text-center text-sm font-medium focus:border-violet-500 focus:ring-2 focus:ring-violet-200 focus:outline-none" />
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-green-900">Upfront Payments (Partner Contributions)</h3>
                    <p className="text-sm text-green-800 mt-1">Optional: Enter upfront payments received from partners. These amounts are added to EBITDA.</p>
                  </div>
                  <button onClick={() => setUpfrontPayments([...upfrontPayments, { id: Date.now(), name: '', y0: 0, y1: 0, y2: 0, y3: 0, y4: 0, y5: 0 }])} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"><Plus className="w-4 h-4" />Add Upfront</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-separate border-spacing-0 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-green-100/70">
                        <th className="px-3 py-2 text-sm font-semibold text-green-900 text-left">Description</th>
                        {['Y0', 'Y1', 'Y2', 'Y3', 'Y4', 'Y5'].map(y => <th key={y} className="px-3 py-2 text-sm font-semibold text-green-900">{y}</th>)}
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {upfrontPayments.map(up => (
                        <tr key={up.id}>
                          <td className="px-1.5 py-1">
                            <input type="text" value={up.name} onChange={(e) => setUpfrontPayments(upfrontPayments.map(u => u.id === up.id ? { ...u, name: e.target.value } : u))} className="w-full px-2 py-2 bg-white border-2 border-green-200 rounded-lg text-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 focus:outline-none" placeholder="e.g., License Fee" />
                          </td>
                          {['y0', 'y1', 'y2', 'y3', 'y4', 'y5'].map(year => (
                            <td key={year} className="px-1.5 py-1">
                              <input type="text" value={up[year] ? '$' + Math.round(up[year]).toLocaleString('en-US') : ''} onChange={(e) => { let val = e.target.value.replace(/\$/g, '').replace(/,/g, ''); if (val === '' || /^\d+$/.test(val)) setUpfrontPayments(upfrontPayments.map(u => u.id === up.id ? { ...u, [year]: val === '' ? 0 : parseInt(val) || 0 } : u)); }} className="w-24 px-2 py-2 bg-white border-2 border-green-200 rounded-lg text-right text-sm focus:border-green-500 focus:ring-2 focus:ring-green-200 focus:outline-none" placeholder="$0" />
                            </td>
                          ))}
                          <td className="px-1.5 py-1 text-center">
                            <button onClick={() => { if (upfrontPayments.length > 1) setUpfrontPayments(upfrontPayments.filter(u => u.id !== up.id)); }} className="p-1.5 rounded-lg bg-red-50 text-red-500 border border-red-200 hover:bg-red-500 hover:text-white disabled:opacity-30" disabled={upfrontPayments.length === 1}><Trash2 className="w-4 h-4" /></button>
                          </td>
                        </tr>
                      ))}
                      {upfrontPayments.length > 1 && (
                        <tr className="bg-green-100/70 font-bold">
                          <td className="px-3 py-2 text-green-900 rounded-l-lg">Total</td>
                          {['y0', 'y1', 'y2', 'y3', 'y4', 'y5'].map(year => (
                            <td key={year} className="px-3 py-2 text-right text-green-900">{formatCurrency(calculations.upfrontTotals[year])}</td>
                          ))}
                          <td className="rounded-r-lg"></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 2 && (
            <div className="space-y-6">
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                <p className="text-[11px] font-bold tracking-widest text-rose-700 uppercase flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> COGS — Cost of Goods Sold per Unit</p>
                <p className="text-xs text-rose-800/70 mt-0.5 mb-3">Each year's change % compounds on the previous year's cost. Negative = savings — e.g., -40 the year a cheaper manufacturer kicks in (shown in green).</p>
                <div className="space-y-2">
                  <div className="grid grid-cols-7 gap-2">
                    <span></span>
                    <span className="text-[11px] font-bold text-rose-800 text-center">COGS / Unit (USD)</span>
                    {['Y1', 'Y2', 'Y3', 'Y4', 'Y5'].map(y => <span key={y} className="text-[11px] font-bold text-rose-700 text-center">Δ {y} %</span>)}
                  </div>
                  {skus.map(sku => (
                    <div key={sku.id} className="grid grid-cols-7 gap-2 items-center">
                      <span className="font-semibold text-slate-700 text-sm truncate pr-2" title={sku.name}>{sku.name || 'Unnamed SKU'}</span>
                      <input type="text" value={sku.cogsPerUnit !== '' ? '$' + sku.cogsPerUnit : ''} onChange={(e) => { const cleaned = e.target.value.replace(/\$/g, '').replace(/,/g, ''); if (cleaned === '' || /^\d*\.?\d{0,2}$/.test(cleaned)) updateSku(sku.id, 'cogsPerUnit', cleaned); }} className="w-full px-2 py-2 bg-white border-2 border-rose-400 rounded-lg text-center text-sm font-bold text-rose-800 focus:border-rose-600 focus:ring-2 focus:ring-rose-200 focus:outline-none" placeholder="$0.00" />
                      {['cogsInflY1', 'cogsInflY2', 'cogsInflY3', 'cogsInflY4', 'cogsInflY5'].map(key => (
                        <input key={key} type="text" value={sku[key] ?? ''} onChange={(e) => { const val = e.target.value; if (val === '' || /^-?\d*\.?\d{0,2}$/.test(val)) updateSku(sku.id, key, val); }} className={`w-full px-2 py-2 bg-white border-2 border-rose-200 rounded-lg text-center text-sm focus:border-rose-500 focus:ring-2 focus:ring-rose-200 focus:outline-none ${parseFloat(sku[key]) < 0 ? 'text-green-600 font-bold border-green-300' : ''}`} placeholder="0" />
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-[11px] font-bold tracking-widest text-orange-700 uppercase flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Operating Expenses</p>
                    <p className="text-xs text-orange-800/70 mt-0.5">"USD" = fixed amount · "%" = percentage of Total Sales (converted to USD in the totals).</p>
                  </div>
                  <button onClick={addExpense} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 shadow-sm"><Plus className="w-4 h-4" />Add Expense</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-separate border-spacing-0 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-orange-100/70">
                        <th className="px-3 py-2 text-sm font-semibold text-orange-900 text-left">Expense Name</th>
                        <th className="px-3 py-2 text-sm font-semibold text-orange-900">Type</th>
                        {['Y0', 'Y1', 'Y2', 'Y3', 'Y4', 'Y5'].map(y => <th key={y} className="px-3 py-2 text-sm font-semibold text-orange-900">{y}</th>)}
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.filter(exp => !(projectInfo.businessModel === 'direct' && exp.name === 'Partner Margin')).map(exp => (
                        <tr key={exp.id}>
                          <td className="px-1.5 py-1"><input type="text" value={exp.name} onChange={(e) => updateExpense(exp.id, 'name', e.target.value)} className="w-full px-2 py-2 bg-white border-2 border-orange-200 rounded-lg text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-200 focus:outline-none" /></td>
                          <td className="px-1.5 py-1"><select value={exp.type} onChange={(e) => updateExpense(exp.id, 'type', e.target.value)} className="w-20 px-2 py-2 bg-white border-2 border-orange-200 rounded-lg text-sm focus:border-orange-500 focus:outline-none"><option value="USD">USD</option><option value="%">%</option></select></td>
                          {['y0', 'y1', 'y2', 'y3', 'y4', 'y5'].map(year => (
                            <td key={year} className="px-1.5 py-1">
                              <input type="text" value={exp.type === 'USD' ? (exp[year] ? '$' + Math.round(exp[year]).toLocaleString('en-US') : '') : exp[year]} onChange={(e) => { let val = e.target.value; if (exp.type === 'USD') { val = val.replace(/\$/g, '').replace(/,/g, ''); if (val === '' || /^\d+$/.test(val)) updateExpense(exp.id, year, val === '' ? 0 : parseInt(val) || 0); } else { if (val === '' || /^\d*\.?\d*$/.test(val)) updateExpense(exp.id, year, val === '' ? 0 : parseFloat(val) || 0); } }} className={`w-24 px-2 py-2 border-2 rounded-lg text-right text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-200 focus:outline-none ${year === 'y0' ? 'bg-orange-100/60 border-orange-300' : 'bg-white border-orange-200'}`} placeholder={exp.type === 'USD' ? '$0' : '0'} />
                            </td>
                          ))}
                          <td className="px-1.5 py-1 text-center"><button onClick={() => removeExpense(exp.id)} className="p-1.5 rounded-lg bg-red-50 text-red-500 border border-red-200 hover:bg-red-500 hover:text-white"><Trash2 className="w-4 h-4" /></button></td>
                        </tr>
                      ))}
                      <tr className="font-semibold">
                        <td className="px-3 py-2 text-sm text-slate-600" colSpan={2}>Total Operating Expenses (USD)</td>
                        {['y0', 'y1', 'y2', 'y3', 'y4', 'y5'].map(year => (
                          <td key={year} className="px-3 py-2 text-right text-sm text-slate-700">{formatCurrency(calculations.totalExpensesUSD[year])}</td>
                        ))}
                        <td></td>
                      </tr>
                      <tr className="font-semibold">
                        <td className="px-3 py-2 text-sm text-green-700" colSpan={2}>Total Operating Expenses (%) → USD</td>
                        {['y0', 'y1', 'y2', 'y3', 'y4', 'y5'].map(year => (
                          <td key={year} className="px-3 py-2 text-right text-sm text-green-700">
                            <span className="text-xs text-green-600">({calculations.totalExpensesPct[year]}%)</span> {formatCurrency(calculations.totalExpensesPctAsUSD[year])}
                          </td>
                        ))}
                        <td></td>
                      </tr>
                      <tr className="font-bold bg-orange-100/70">
                        <td className="px-3 py-2 text-orange-900 rounded-l-lg" colSpan={2}>Total</td>
                        {['y0', 'y1', 'y2', 'y3', 'y4', 'y5'].map(year => (
                          <td key={year} className="px-3 py-2 text-right text-orange-900">{formatCurrency(calculations.totalExpenses[year])}</td>
                        ))}
                        <td className="rounded-r-lg"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 3 && (
            <div className="space-y-6" id="pl-content">
              {/* Missing-inputs hint (never printed: pl-content is captured for the PDF) */}
              {(() => {
                const gaps = [];
                if (!stepDone[0]) gaps.push('historical units for at least one SKU (step 1)');
                if (!skus.some(s => parseFloat(s.aspPerUnit) > 0 || ['aspY1', 'aspY2', 'aspY3', 'aspY4', 'aspY5'].some(k => parseFloat(s[k]) > 0))) gaps.push('ASP — selling price (step 2)');
                if (!skus.some(s => ['msY1', 'msY2', 'msY3', 'msY4', 'msY5'].some(k => parseFloat(s[k]) > 0))) gaps.push('Luye market share % (step 2)');
                if (!stepDone[2]) gaps.push('COGS per unit (step 3)');
                if (gaps.length === 0) return null;
                return (
                  <div className={`bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 ${isPrinting ? 'hidden' : ''}`}>
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800"><span className="font-semibold">Some figures show $0 because data is missing:</span> {gaps.join(' · ')}.</p>
                  </div>
                );
              })()}
              <div className={`flex justify-end ${isPrinting ? 'hidden' : ''}`}>
                <button onClick={handleExportPDF} disabled={isGeneratingPDF} className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-md ${isGeneratingPDF ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}>
                  {isGeneratingPDF ? (<><svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Generating PDF...</>) : (<><Download className="w-5 h-5" />Export to PDF</>)}
                </button>
              </div>

              <div className={`mb-6 border-b-2 border-blue-600 pb-4 ${isPrinting ? 'block' : 'hidden'}`}>
                <h1 className="text-2xl font-bold text-slate-800">Business Case - P&L Report</h1>
                <p className="text-slate-600">Luye Pharma LATAM Financial Evaluation</p>
                <div className="flex gap-6 mt-2 text-sm text-slate-700">
                  <span><strong>Project:</strong> {projectInfo.name || 'N/A'}</span>
                  <span><strong>Country:</strong> {projectInfo.country}</span>
                  <span><strong>Product:</strong> {projectInfo.product || 'N/A'}</span>
                  <span><strong>Partner:</strong> {projectInfo.partner || 'N/A'}</span>
                  <span><strong>Base Year:</strong> {projectInfo.year}</span>
                  <span><strong>Generated:</strong> {new Date().toLocaleDateString()}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <div onClick={() => setChartFilters({...chartFilters, sales: !chartFilters.sales})} className={`bg-gradient-to-br from-green-50 to-white rounded-lg shadow-md border-2 p-6 cursor-pointer transition-all ${(chartFilters.sales || isPrinting) ? 'border-green-500 shadow-lg scale-105' : 'border-green-200 hover:shadow-lg'}`}>
                  <h3 className="text-sm font-semibold text-green-600 mb-2">Total Sales (5 Years)</h3>
                  <p className="text-3xl font-bold text-green-700">{formatCurrency(calculations.accum5yr.sales)}</p>
                  {(chartFilters.sales || isPrinting) && <p className="text-xs text-green-600 mt-2">● Showing in chart</p>}
                </div>
                <div onClick={() => setChartFilters({...chartFilters, grossProfit: !chartFilters.grossProfit})} className={`bg-gradient-to-br from-blue-50 to-white rounded-lg shadow-md border-2 p-6 cursor-pointer transition-all ${(chartFilters.grossProfit || isPrinting) ? 'border-blue-500 shadow-lg scale-105' : 'border-blue-200 hover:shadow-lg'}`}>
                  <h3 className="text-sm font-semibold text-blue-600 mb-2">Gross Profit (5 Years)</h3>
                  <p className="text-3xl font-bold text-blue-700">{formatCurrency(calculations.accum5yr.gp)}</p>
                  {(chartFilters.grossProfit || isPrinting) && <p className="text-xs text-blue-600 mt-2">● Showing in chart</p>}
                </div>
                <div onClick={() => setChartFilters({...chartFilters, expenses: !chartFilters.expenses})} className={`bg-gradient-to-br from-orange-50 to-white rounded-lg shadow-md border-2 p-6 cursor-pointer transition-all ${(chartFilters.expenses || isPrinting) ? 'border-orange-500 shadow-lg scale-105' : 'border-orange-200 hover:shadow-lg'}`}>
                  <h3 className="text-sm font-semibold text-orange-600 mb-2">Total Expenses (5 Years)</h3>
                  <p className="text-3xl font-bold text-orange-700">{formatCurrency(calculations.accum5yr.cogs + calculations.accum5yr.expenses)}</p>
                  {(chartFilters.expenses || isPrinting) && <p className="text-xs text-orange-600 mt-2">● Showing in chart</p>}
                </div>
                <div onClick={() => setChartFilters({...chartFilters, ebitda: !chartFilters.ebitda})} className={`rounded-lg shadow-md border-2 p-6 cursor-pointer transition-all ${calculations.accum5yr.ebitda > 0 ? ((chartFilters.ebitda || isPrinting) ? 'bg-gradient-to-br from-purple-50 to-white border-purple-500 shadow-lg scale-105' : 'bg-gradient-to-br from-purple-50 to-white border-purple-200 hover:shadow-lg') : ((chartFilters.ebitda || isPrinting) ? 'bg-gradient-to-br from-red-50 to-white border-red-500 shadow-lg scale-105' : 'bg-gradient-to-br from-red-50 to-white border-red-200 hover:shadow-lg')}`}>
                  <h3 className={`text-sm font-semibold mb-2 ${calculations.accum5yr.ebitda > 0 ? 'text-purple-600' : 'text-red-600'}`}>EBITDA (5 Years)</h3>
                  <p className={`text-3xl font-bold ${calculations.accum5yr.ebitda > 0 ? 'text-purple-700' : 'text-red-700'}`}>{formatCurrency(calculations.accum5yr.ebitda)}</p>
                  {(chartFilters.ebitda || isPrinting) && <p className={`text-xs mt-2 ${calculations.accum5yr.ebitda > 0 ? 'text-purple-600' : 'text-red-600'}`}>● Showing in chart</p>}
                </div>
                <div title="Net Present Value — the EBITDA of each year discounted at the WACC rate. With WACC at 0 it equals the accumulated EBITDA." className={`rounded-lg shadow-md border-2 p-6 ${calculations.npvTotal >= 0 ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-500' : 'bg-gradient-to-br from-red-50 to-white border-red-500'}`}>
                  <h3 className={`text-sm font-semibold mb-2 ${calculations.npvTotal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>NPV <span className="text-xs font-normal">(WACC: {projectInfo.wacc}%)</span></h3>
                  <p className={`text-3xl font-bold ${calculations.npvTotal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatCurrency(calculations.npvTotal)}</p>
                  <p className={`text-xs mt-2 ${calculations.npvTotal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{calculations.npvTotal >= 0 ? '✓ Value creating' : '✗ Value destroying'}</p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6">
                <h3 className="font-bold text-slate-800 mb-2">Revenue & EBITDA Projection</h3>
                <p className={`text-sm text-slate-500 mb-4 ${isPrinting ? 'hidden' : ''}`}>Click on the cards above to show/hide data series in the chart</p>
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={[
                    { year: 'Y0', sales: 0, grossProfit: 0, expenses: calculations.totalExpenses.y0, ebitda: calculations.ebitda.y0 },
                    { year: 'Y1', sales: calculations.grandTotals.sales.y1, grossProfit: calculations.grandTotals.gp.y1, expenses: calculations.grandTotals.totalCogs.y1 + calculations.totalExpenses.y1, ebitda: calculations.ebitda.y1 },
                    { year: 'Y2', sales: calculations.grandTotals.sales.y2, grossProfit: calculations.grandTotals.gp.y2, expenses: calculations.grandTotals.totalCogs.y2 + calculations.totalExpenses.y2, ebitda: calculations.ebitda.y2 },
                    { year: 'Y3', sales: calculations.grandTotals.sales.y3, grossProfit: calculations.grandTotals.gp.y3, expenses: calculations.grandTotals.totalCogs.y3 + calculations.totalExpenses.y3, ebitda: calculations.ebitda.y3 },
                    { year: 'Y4', sales: calculations.grandTotals.sales.y4, grossProfit: calculations.grandTotals.gp.y4, expenses: calculations.grandTotals.totalCogs.y4 + calculations.totalExpenses.y4, ebitda: calculations.ebitda.y4 },
                    { year: 'Y5', sales: calculations.grandTotals.sales.y5, grossProfit: calculations.grandTotals.gp.y5, expenses: calculations.grandTotals.totalCogs.y5 + calculations.totalExpenses.y5, ebitda: calculations.ebitda.y5 }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    {(chartFilters.sales || isPrinting) && <Bar dataKey="sales" fill="#22c55e" name="Total Sales" />}
                    {(chartFilters.grossProfit || isPrinting) && <Bar dataKey="grossProfit" fill="#3b82f6" name="Gross Profit" />}
                    {(chartFilters.expenses || isPrinting) && <Bar dataKey="expenses" fill="#f97316" name="Total Expenses" />}
                    {(chartFilters.ebitda || isPrinting) && <Line type="monotone" dataKey="ebitda" stroke="#8b5cf6" strokeWidth={3} name="EBITDA" />}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {calculations.privateResults.length > 0 && (
                <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6">
                  <h3 className="font-bold text-slate-800 mb-4">Private Market P&L</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-300">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="border border-slate-300 px-4 py-2 text-left font-semibold">Line Item</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y0</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y1</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y2</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y3</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y4</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y5</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold bg-blue-50">Total 5Y</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-300 px-4 py-2 font-semibold">Units</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">0</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.privateTotals.units.y1)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.privateTotals.units.y2)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.privateTotals.units.y3)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.privateTotals.units.y4)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.privateTotals.units.y5)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right bg-blue-50 font-semibold">{formatNumber(calculations.privateTotals.units.y1 + calculations.privateTotals.units.y2 + calculations.privateTotals.units.y3 + calculations.privateTotals.units.y4 + calculations.privateTotals.units.y5)}</td>
                        </tr>
                        {projectInfo.businessModel === 'partner' && (
                          <>
                            <tr className="bg-green-50">
                              <td className="border border-slate-300 px-4 py-2 font-semibold">Partner Sales</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(0)}</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.privateTotals.sales.y1)}</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.privateTotals.sales.y2)}</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.privateTotals.sales.y3)}</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.privateTotals.sales.y4)}</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.privateTotals.sales.y5)}</td>
                              <td className="border border-slate-300 px-4 py-2 text-right bg-green-100 font-semibold">{formatCurrency(calculations.privateTotals.sales.y1 + calculations.privateTotals.sales.y2 + calculations.privateTotals.sales.y3 + calculations.privateTotals.sales.y4 + calculations.privateTotals.sales.y5)}</td>
                            </tr>
                            <tr className="bg-orange-50">
                              <td className="border border-slate-300 px-4 py-2 font-semibold">Partner Margin %</td>
                              <td className="border border-slate-300 px-4 py-2 text-right text-slate-400">—</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{calculations.partnerMarginPct.y1}%</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{calculations.partnerMarginPct.y2}%</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{calculations.partnerMarginPct.y3}%</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{calculations.partnerMarginPct.y4}%</td>
                              <td className="border border-slate-300 px-4 py-2 text-right">{calculations.partnerMarginPct.y5}%</td>
                              <td className="border border-slate-300 px-4 py-2 text-right bg-blue-50"></td>
                            </tr>
                            <tr>
                              <td className="border border-slate-300 px-4 py-2 font-semibold text-red-600">Partner Margin (USD)</td>
                              <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(0)})</td>
                              <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.privatePartnerMargin.y1)})</td>
                              <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.privatePartnerMargin.y2)})</td>
                              <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.privatePartnerMargin.y3)})</td>
                              <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.privatePartnerMargin.y4)})</td>
                              <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.privatePartnerMargin.y5)})</td>
                              <td className="border border-slate-300 px-4 py-2 text-right bg-blue-50 font-semibold text-red-600">({formatCurrency(calculations.privatePartnerMargin.y1 + calculations.privatePartnerMargin.y2 + calculations.privatePartnerMargin.y3 + calculations.privatePartnerMargin.y4 + calculations.privatePartnerMargin.y5)})</td>
                            </tr>
                          </>
                        )}
                        <tr className="bg-blue-50">
                          <td className="border border-slate-300 px-4 py-2 font-bold">Luye Sales</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(0)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.privateLuyeSales.y1)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.privateLuyeSales.y2)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.privateLuyeSales.y3)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.privateLuyeSales.y4)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.privateLuyeSales.y5)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right bg-blue-100 font-bold">{formatCurrency(calculations.privateLuyeSales.y1 + calculations.privateLuyeSales.y2 + calculations.privateLuyeSales.y3 + calculations.privateLuyeSales.y4 + calculations.privateLuyeSales.y5)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {calculations.publicResults.length > 0 && (
                <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6">
                  <h3 className="font-bold text-slate-800 mb-4">Public Market P&L</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-300">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="border border-slate-300 px-4 py-2 text-left font-semibold">Line Item</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y0</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y1</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y2</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y3</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y4</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y5</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold bg-blue-50">Total 5Y</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-300 px-4 py-2 font-semibold">Units</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">0</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.publicTotals.units.y1)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.publicTotals.units.y2)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.publicTotals.units.y3)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.publicTotals.units.y4)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.publicTotals.units.y5)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right bg-blue-50 font-semibold">{formatNumber(calculations.publicTotals.units.y1 + calculations.publicTotals.units.y2 + calculations.publicTotals.units.y3 + calculations.publicTotals.units.y4 + calculations.publicTotals.units.y5)}</td>
                        </tr>
                        <tr className="bg-green-50">
                          <td className="border border-slate-300 px-4 py-2 font-semibold">Total Sales</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(0)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.publicTotals.sales.y1)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.publicTotals.sales.y2)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.publicTotals.sales.y3)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.publicTotals.sales.y4)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.publicTotals.sales.y5)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right bg-green-100 font-semibold">{formatCurrency(calculations.publicTotals.sales.y1 + calculations.publicTotals.sales.y2 + calculations.publicTotals.sales.y3 + calculations.publicTotals.sales.y4 + calculations.publicTotals.sales.y5)}</td>
                        </tr>
                        <tr>
                          <td className="border border-slate-300 px-4 py-2 font-semibold">COGS</td>
                          <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(0)})</td>
                          <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.publicTotals.totalCogs.y1)})</td>
                          <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.publicTotals.totalCogs.y2)})</td>
                          <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.publicTotals.totalCogs.y3)})</td>
                          <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.publicTotals.totalCogs.y4)})</td>
                          <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.publicTotals.totalCogs.y5)})</td>
                          <td className="border border-slate-300 px-4 py-2 text-right bg-blue-50 font-semibold text-red-600">({formatCurrency(calculations.publicTotals.totalCogs.y1 + calculations.publicTotals.totalCogs.y2 + calculations.publicTotals.totalCogs.y3 + calculations.publicTotals.totalCogs.y4 + calculations.publicTotals.totalCogs.y5)})</td>
                        </tr>
                        <tr className="bg-blue-50">
                          <td className="border border-slate-300 px-4 py-2 font-bold">Gross Profit</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(0)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.publicTotals.gp.y1)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.publicTotals.gp.y2)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.publicTotals.gp.y3)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.publicTotals.gp.y4)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.publicTotals.gp.y5)}</td>
                          <td className="border border-slate-300 px-4 py-2 text-right bg-blue-100 font-bold">{formatCurrency(calculations.publicTotals.gp.y1 + calculations.publicTotals.gp.y2 + calculations.publicTotals.gp.y3 + calculations.publicTotals.gp.y4 + calculations.publicTotals.gp.y5)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {calculations.hybridResults.length > 0 && (
                <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6">
                  <h3 className="font-bold text-slate-800 mb-4">Hybrid Market P&L</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-300">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="border border-slate-300 px-4 py-2 text-left font-semibold">Line Item</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y0</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y1</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y2</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y3</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y4</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y5</th>
                          <th className="border border-slate-300 px-4 py-2 text-right font-semibold bg-blue-50">Total 5Y</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-300 px-4 py-2 font-semibold">Units</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">0</td>
                          {['y1', 'y2', 'y3', 'y4', 'y5'].map(y => <td key={y} className="border border-slate-300 px-4 py-2 text-right">{formatNumber(calculations.hybridTotals.units[y])}</td>)}
                          <td className="border border-slate-300 px-4 py-2 text-right bg-blue-50 font-semibold">{formatNumber(calculations.hybridTotals.units.y1 + calculations.hybridTotals.units.y2 + calculations.hybridTotals.units.y3 + calculations.hybridTotals.units.y4 + calculations.hybridTotals.units.y5)}</td>
                        </tr>
                        <tr className="bg-green-50">
                          <td className="border border-slate-300 px-4 py-2 font-semibold">Total Sales</td>
                          <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(0)}</td>
                          {['y1', 'y2', 'y3', 'y4', 'y5'].map(y => <td key={y} className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.hybridTotals.sales[y])}</td>)}
                          <td className="border border-slate-300 px-4 py-2 text-right bg-green-100 font-semibold">{formatCurrency(calculations.hybridTotals.sales.y1 + calculations.hybridTotals.sales.y2 + calculations.hybridTotals.sales.y3 + calculations.hybridTotals.sales.y4 + calculations.hybridTotals.sales.y5)}</td>
                        </tr>
                        <tr>
                          <td className="border border-slate-300 px-4 py-2 font-semibold">COGS</td>
                          <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(0)})</td>
                          {['y1', 'y2', 'y3', 'y4', 'y5'].map(y => <td key={y} className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.hybridTotals.totalCogs[y])})</td>)}
                          <td className="border border-slate-300 px-4 py-2 text-right bg-blue-50 font-semibold text-red-600">({formatCurrency(calculations.hybridTotals.totalCogs.y1 + calculations.hybridTotals.totalCogs.y2 + calculations.hybridTotals.totalCogs.y3 + calculations.hybridTotals.totalCogs.y4 + calculations.hybridTotals.totalCogs.y5)})</td>
                        </tr>
                        <tr className="bg-blue-50">
                          <td className="border border-slate-300 px-4 py-2 font-bold">Gross Profit</td>
                          <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(0)}</td>
                          {['y1', 'y2', 'y3', 'y4', 'y5'].map(y => <td key={y} className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(calculations.hybridTotals.gp[y])}</td>)}
                          <td className="border border-slate-300 px-4 py-2 text-right bg-blue-100 font-bold">{formatCurrency(calculations.hybridTotals.gp.y1 + calculations.hybridTotals.gp.y2 + calculations.hybridTotals.gp.y3 + calculations.hybridTotals.gp.y4 + calculations.hybridTotals.gp.y5)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6">
                <h3 className="font-bold text-slate-800 mb-4">Consolidated P&L (Total)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-slate-300">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="border border-slate-300 px-4 py-2 text-left font-semibold">Line Item</th>
                        <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y0</th>
                        <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y1</th>
                        <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y2</th>
                        <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y3</th>
                        <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y4</th>
                        <th className="border border-slate-300 px-4 py-2 text-right font-semibold">Y5</th>
                        <th className="border border-slate-300 px-4 py-2 text-right font-semibold bg-purple-50">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-green-50">
                        <td className="border border-slate-300 px-4 py-2 font-semibold">Total Luye Sales</td>
                        <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(0)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.totalLuyeSales.y1)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.totalLuyeSales.y2)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.totalLuyeSales.y3)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.totalLuyeSales.y4)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right">{formatCurrency(calculations.totalLuyeSales.y5)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right bg-green-100 font-bold">{formatCurrency(calculations.totalLuyeSales.y1 + calculations.totalLuyeSales.y2 + calculations.totalLuyeSales.y3 + calculations.totalLuyeSales.y4 + calculations.totalLuyeSales.y5)}</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-300 px-4 py-2 font-semibold">Total COGS</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(0)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.grandTotals.totalCogs.y1)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.grandTotals.totalCogs.y2)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.grandTotals.totalCogs.y3)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.grandTotals.totalCogs.y4)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.grandTotals.totalCogs.y5)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right bg-purple-50 font-bold text-red-600">({formatCurrency(calculations.accum5yr.cogs)})</td>
                      </tr>
                      <tr className="bg-blue-50">
                        <td className="border border-slate-300 px-4 py-2 font-bold">Gross Profit</td>
                        <td className="border border-slate-300 px-4 py-2 text-right font-bold">{formatCurrency(0)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.totalLuyeGP.y1 >= 0 ? '' : 'text-red-700'}`}>{formatCurrency(calculations.totalLuyeGP.y1)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.totalLuyeGP.y2 >= 0 ? '' : 'text-red-700'}`}>{formatCurrency(calculations.totalLuyeGP.y2)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.totalLuyeGP.y3 >= 0 ? '' : 'text-red-700'}`}>{formatCurrency(calculations.totalLuyeGP.y3)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.totalLuyeGP.y4 >= 0 ? '' : 'text-red-700'}`}>{formatCurrency(calculations.totalLuyeGP.y4)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.totalLuyeGP.y5 >= 0 ? '' : 'text-red-700'}`}>{formatCurrency(calculations.totalLuyeGP.y5)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right bg-blue-100 font-bold ${(calculations.totalLuyeGP.y1 + calculations.totalLuyeGP.y2 + calculations.totalLuyeGP.y3 + calculations.totalLuyeGP.y4 + calculations.totalLuyeGP.y5) >= 0 ? '' : 'text-red-700'}`}>{formatCurrency(calculations.totalLuyeGP.y1 + calculations.totalLuyeGP.y2 + calculations.totalLuyeGP.y3 + calculations.totalLuyeGP.y4 + calculations.totalLuyeGP.y5)}</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-300 px-4 py-2 font-semibold">Upfront Payment</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-green-600">{formatCurrency(calculations.upfrontTotals.y0)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-green-600">{formatCurrency(calculations.upfrontTotals.y1)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-green-600">{formatCurrency(calculations.upfrontTotals.y2)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-green-600">{formatCurrency(calculations.upfrontTotals.y3)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-green-600">{formatCurrency(calculations.upfrontTotals.y4)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-green-600">{formatCurrency(calculations.upfrontTotals.y5)}</td>
                        <td className="border border-slate-300 px-4 py-2 text-right bg-purple-50 font-bold text-green-600">{formatCurrency(calculations.accum5yr.upfront)}</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-300 px-4 py-2 font-semibold">Operating Expenses</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.totalExpenses.y0)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.totalExpenses.y1)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.totalExpenses.y2)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.totalExpenses.y3)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.totalExpenses.y4)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right text-red-600">({formatCurrency(calculations.totalExpenses.y5)})</td>
                        <td className="border border-slate-300 px-4 py-2 text-right bg-purple-50 font-bold text-red-600">({formatCurrency(calculations.accum5yr.expenses)})</td>
                      </tr>
                      <tr className="bg-blue-50">
                        <td className="border border-slate-300 px-4 py-2 font-bold text-lg">EBITDA</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.ebitda.y0 >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatCurrency(calculations.ebitda.y0)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.ebitda.y1 >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatCurrency(calculations.ebitda.y1)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.ebitda.y2 >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatCurrency(calculations.ebitda.y2)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.ebitda.y3 >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatCurrency(calculations.ebitda.y3)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.ebitda.y4 >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatCurrency(calculations.ebitda.y4)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.ebitda.y5 >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatCurrency(calculations.ebitda.y5)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold text-lg bg-blue-100 ${calculations.accum5yr.ebitda >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatCurrency(calculations.accum5yr.ebitda)}</td>
                      </tr>
                      <tr className="bg-purple-50">
                        <td className="border border-slate-300 px-4 py-2 font-bold text-lg">NPV <span className="text-xs font-normal text-purple-600">(WACC: {projectInfo.wacc}%)</span></td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.npvByYear.y0 >= 0 ? 'text-purple-700' : 'text-red-700'}`}>{formatCurrency(calculations.npvByYear.y0)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.npvByYear.y1 >= 0 ? 'text-purple-700' : 'text-red-700'}`}>{formatCurrency(calculations.npvByYear.y1)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.npvByYear.y2 >= 0 ? 'text-purple-700' : 'text-red-700'}`}>{formatCurrency(calculations.npvByYear.y2)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.npvByYear.y3 >= 0 ? 'text-purple-700' : 'text-red-700'}`}>{formatCurrency(calculations.npvByYear.y3)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.npvByYear.y4 >= 0 ? 'text-purple-700' : 'text-red-700'}`}>{formatCurrency(calculations.npvByYear.y4)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold ${calculations.npvByYear.y5 >= 0 ? 'text-purple-700' : 'text-red-700'}`}>{formatCurrency(calculations.npvByYear.y5)}</td>
                        <td className={`border border-slate-300 px-4 py-2 text-right font-bold text-lg bg-purple-100 ${calculations.npvTotal >= 0 ? 'text-purple-700' : 'text-red-700'}`}>{formatCurrency(calculations.npvTotal)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Step navigation */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t border-slate-200">
              {activeTab > 0 ? (
                <button onClick={() => setActiveTab(activeTab - 1)} className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium">
                  <ArrowLeft className="w-4 h-4" /> Back: {tabs[activeTab - 1].name}
                </button>
              ) : <span />}
              {activeTab < 3 && (
                <button onClick={() => setActiveTab(activeTab + 1)} className={`flex items-center gap-2 px-5 py-2.5 text-white rounded-lg font-medium shadow-md ${activeTab === 2 ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                  {activeTab === 2 ? 'View Results' : `Next: ${tabs[activeTab + 1].name}`} <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
        </div>
      </div>
    </div>
  );
};
